import { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/spizirnaUi';

/**
 * Živý náhled produktu na eshopu (2026-10-26) — karta v menu a detail jídla
 * tak, jak je uvidí zákazník na fofrlama.cz. Mění se hned při psaní názvu,
 * popisu nebo výběru fotky; ceny jsou ze záložky Ceny podle druhu objednávky
 * (cena kanálu, jinak obecná — stejné pravidlo jako eshop).
 *
 * Barvy jsou natvrdo podle webu (tmavá břidlice, oranžová), ne podle motivu
 * OS: náhled má ukázat skutečný vzhled eshopu.
 */
const DRUHY = [
    { klic: 'rozvoz', nazev: 'Rozvoz' },
    { klic: 'vydej', nazev: 'Vyzvednu si' },
    { klic: 'bistro', nazev: 'U stolu' },
];

const W = {
    bg: '#111111', karta: '#1B1A1C', karta2: '#262428', linka: 'rgba(255,255,255,0.12)',
    tx: '#F6F3F0', tx2: '#C8BFB8', tx3: '#8E8680', oranzova: '#FF8A00', cokolada: '#1C0D00',
    nadpis: 'Anton, "Arial Narrow", Impact, sans-serif', text: 'Montserrat, "Segoe UI", sans-serif',
};

const kc = (c) => (c == null ? '—' : `${Math.round(c)} Kč`);
// „02. Šunková" → „Šunková": číslo je pořadí v menu, eshop ho neukazuje.
const bezCisla = (n) => (n || '').replace(/^\d+\s*\.\s*/u, '').trim();

export default function EshopNahled({ receptId, nazev, popis, fotoUrl, mutedColor, obnovit = 0 }) {
    const [data, setData] = useState(null);
    const [chyba, setChyba] = useState(false);
    const [druh, setDruh] = useState('rozvoz');
    const [vybrana, setVybrana] = useState(null);

    useEffect(() => {
        let platne = true;
        setChyba(false);
        api(`/api/recepty/recepty/${receptId}/eshop-nahled`)
            .then((d) => { if (platne) setData(d); })
            .catch(() => { if (platne) setChyba(true); });
        return () => { platne = false; };
    }, [receptId, obnovit]);

    const varianty = useMemo(() => (data?.varianty || []).filter((v) => v.aktivni), [data]);
    const sCenou = varianty.filter((v) => v.ceny[druh] != null);
    const cenaOd = sCenou.length ? Math.min(...sCenou.map((v) => v.ceny[druh])) : null;
    const aktivni = sCenou.find((v) => v.id === vybrana) || sCenou.find((v) => v.primarni) || sCenou[0];
    const jmeno = bezCisla(nazev) || 'Název produktu';

    const foto = (vyska) => fotoUrl ? (
        <img src={fotoUrl} alt="" style={{ height: vyska, maxWidth: '100%', objectFit: 'contain', filter: 'drop-shadow(0 10px 14px rgba(0,0,0,.55))' }} />
    ) : (
        <div style={{ height: vyska, width: vyska * 1.1, borderRadius: 12, border: `1px dashed ${W.linka}`, display: 'grid', placeItems: 'center', color: W.tx3, fontSize: 11, textAlign: 'center' }}>bez fotky</div>
    );

    return (
        <div>
            <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: mutedColor }}>Náhled na eshopu</span>
                <div className="flex gap-1">
                    {DRUHY.map((d) => (
                        <button key={d.klic} type="button" onClick={() => setDruh(d.klic)}
                            className="text-[11px] font-semibold px-2 py-1 rounded-full"
                            style={{ background: druh === d.klic ? W.oranzova : 'rgba(127,127,127,0.12)', color: druh === d.klic ? W.cokolada : mutedColor }}>
                            {d.nazev}
                        </button>
                    ))}
                </div>
            </div>

            {data && !data.kategorie_na_eshopu && (
                <p className="text-xs mb-2 px-3 py-2 rounded-lg" style={{ background: 'rgba(245,158,11,0.12)', color: '#F59E0B' }}>
                    Kategorie {data.kategorie ? `„${data.kategorie}“ ` : ''}není na eshopu zapnutá — zákazník produkt zatím neuvidí.
                    Zapíná se v detailu kategorie (viditelnost E-shop).
                </p>
            )}
            {chyba && <p className="text-xs mb-2" style={{ color: '#EF4444' }}>Ceny pro náhled se nepodařilo načíst.</p>}

            <div style={{ background: W.bg, borderRadius: 16, padding: 14, fontFamily: W.text, color: W.tx, display: 'grid', gap: 14 }}>
                {/* Karta v menu */}
                <div>
                    <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: W.tx3, marginBottom: 6 }}>V menu</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 96px', gap: 10, alignItems: 'center', padding: 12, borderRadius: 16, background: W.karta, border: `1px solid ${W.linka}` }}>
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 800, fontSize: 14 }}>{jmeno}</div>
                            <div style={{ color: W.tx3, fontSize: 11.5, lineHeight: 1.4, marginTop: 3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                {popis?.trim() || <i style={{ opacity: 0.6 }}>bez popisu</i>}
                            </div>
                            <div style={{ fontWeight: 900, fontSize: 14, marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                                {cenaOd == null ? <span style={{ color: '#EF4444', fontSize: 12 }}>bez ceny — neprodává se</span> : `${sCenou.length > 1 ? 'od ' : ''}${kc(cenaOd)}`}
                                <span style={{ width: 22, height: 22, borderRadius: '50%', background: W.oranzova, color: W.cokolada, display: 'grid', placeItems: 'center', fontWeight: 900 }}>+</span>
                            </div>
                        </div>
                        <div style={{ display: 'grid', placeItems: 'center' }}>{foto(80)}</div>
                    </div>
                </div>

                {/* Detail jídla */}
                <div>
                    <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: W.tx3, marginBottom: 6 }}>Detail jídla</div>
                    <div style={{ borderRadius: 16, background: W.karta, border: `1px solid ${W.linka}`, overflow: 'hidden' }}>
                        <div style={{ height: 150, display: 'grid', placeItems: 'center', background: 'radial-gradient(60% 70% at 50% 40%, rgba(255,138,0,.22), transparent 70%)' }}>{foto(130)}</div>
                        <div style={{ padding: '4px 14px 14px', display: 'grid', gap: 10 }}>
                            <div style={{ fontFamily: W.nadpis, fontSize: 24, lineHeight: 1, textTransform: 'uppercase' }}>{jmeno}</div>
                            {popis?.trim() && <div style={{ color: W.tx2, fontSize: 12.5, lineHeight: 1.5, whiteSpace: 'pre-line' }}>{popis.trim()}</div>}
                            {sCenou.length > 1 && (
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                    {sCenou.map((v) => (
                                        <button key={v.id} type="button" onClick={() => setVybrana(v.id)}
                                            style={{ flex: '1 1 70px', padding: '7px 4px', borderRadius: 10, fontSize: 11.5, fontWeight: 700, lineHeight: 1.3,
                                                border: `1px solid ${aktivni?.id === v.id ? W.oranzova : W.linka}`,
                                                background: aktivni?.id === v.id ? 'rgba(255,138,0,.12)' : 'transparent', color: aktivni?.id === v.id ? W.tx : W.tx2 }}>
                                            {v.nazev}<br />{kc(v.ceny[druh])}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: 42, padding: '0 14px', borderRadius: 13, fontWeight: 800, fontSize: 13,
                                background: aktivni ? `linear-gradient(180deg, #FFA02A, ${W.oranzova})` : W.karta2, color: aktivni ? W.cokolada : W.tx3 }}>
                                <span>Přidat do košíku</span><span>{aktivni ? kc(aktivni.ceny[druh]) : '—'}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <p className="text-[11px] mt-2" style={{ color: mutedColor }}>
                Ceny jsou ze záložky Ceny pro zvolený druh objednávky. Přídavky a combo se v eshopu nabídnou podle záložek Ingredience a Combo.
            </p>
        </div>
    );
}
